import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';
import Alert from '../../common/components/Alert';
import {
  getButtonSizeStyle,
  getButtonVariantStyle,
} from '../../common/components/form/buttonStyles';
import linkStyles from '../../common/components/linkStyles';
import PageLoading from '../../common/components/loading/PageLoading';
import Modal, { ModalRef } from '../../common/components/modals/Modal';
import { BORDER_RADIUS, BORDER_WIDTH } from '../../common/styles/borders';
import { GREYSCALE } from '../../common/styles/colors';
import { SPACING } from '../../common/styles/spacing';
import { TYPOGRAPHY } from '../../common/styles/typography';
import convertBbcode from '../../common/utils/convertBbcode';
import withOpacity from '../../common/utils/withOpacity';
import { useLazyIntercomArticleQuery } from '../operations/queries/intercomArticle.query';

const tableHeadings = css`
  font-weight: ${TYPOGRAPHY.fontWeight.medium};
  padding: ${SPACING.lg} ${SPACING.md};
  background-color: ${GREYSCALE.grey20};

  p {
    color: ${GREYSCALE.grey60};
  }
`;

const Styled = {
  IntercomArticle: styled.div`
    h1.text-2xl {
      font-size: ${TYPOGRAPHY.heading.xxl.fontSize};
      font-weight: ${TYPOGRAPHY.heading.xxl.fontWeight};
      line-height: ${TYPOGRAPHY.heading.xxl.lineHeight};
      margin: ${SPACING.none} ${SPACING.xl} ${SPACING.none} ${SPACING.none};
    }

    .text-body-secondary-color {
      color: ${GREYSCALE.grey50};
    }

    .table-of-contents {
      display: none;
    }

    .flex {
      display: flex;
    }

    .flex-col {
      flex-direction: column;
    }

    .avatar {
      font-size: ${TYPOGRAPHY.fontSize.sm};
      margin: 0px 0px 10px;
    }

    .article_body {
      margin: ${SPACING.xl} ${SPACING.none} ${SPACING.none};

      .intercom-interblocks-heading h1 {
        font-size: ${TYPOGRAPHY.heading.xl.fontSize};
        font-weight: ${TYPOGRAPHY.heading.xl.fontWeight};
        line-height: ${TYPOGRAPHY.heading.xl.lineHeight};
        margin: ${SPACING.none} ${SPACING.none} ${SPACING.lg};
      }

      .intercom-interblocks-subheading h2,
      .intercom-interblocks-subheading3 h3 {
        font-size: ${TYPOGRAPHY.heading.md.fontSize};
        font-weight: ${TYPOGRAPHY.heading.md.fontWeight};
        line-height: ${TYPOGRAPHY.heading.md.lineHeight};
        margin: ${SPACING.md} ${SPACING.none} ${SPACING.none};
      }

      .intercom-interblocks-callout {
      padding: ${SPACING.md};
      border-radius: ${BORDER_RADIUS.xs};
      margin: ${SPACING.xl} 0;
      box-shadow: inset 0 1px 0 ${withOpacity(GREYSCALE.white, 0.25)},
        0 1px 2px ${withOpacity(GREYSCALE.black, 0.05)};
    }

    .intercom-interblocks-paragraph {
        &.no-margin p {
          margin: ${SPACING.none};
        }
      }

      .intercom-interblocks-align-center {
        text-align: center;
      }

      .intercom-interblocks-video > iframe {
        width: 100%;
        height: 480px;
      }

      .intercom-interblocks-button a {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        text-align: center;
        vertical-align: middle;
        border: ${BORDER_WIDTH.sm} solid transparent;
        text-decoration: none;
        margin: ${SPACING.md} 0;
        cursor: pointer;

        ${getButtonSizeStyle('medium')}
        ${getButtonVariantStyle('primary')}
        :active {
          box-shadow: inset ${SPACING.none} ${SPACING.xs} ${SPACING.sm} ${withOpacity(
    GREYSCALE.black,
    0.125,
  )};
        }
      }

      .intercom-interblocks-image img {
        max-width: 100%;
        height: auto;
      }

      p {
        margin: 1em 0;
        font-size: ${TYPOGRAPHY.fontSize.md};
      }

      a {
        ${linkStyles}
      }

      .intercom-interblocks-table {
        border-style: solid;
        border-color: ${GREYSCALE.grey30};
        border-width: ${BORDER_WIDTH.sm};
        border-radius: ${BORDER_RADIUS.sm};
        margin-bottom: ${SPACING.xxl};

        table {
          width: 100%;
          border-collapse: collapse;
          font-size: ${TYPOGRAPHY.fontSize.sm};
        }

        td {
          padding: ${SPACING.md};
          border-color: ${GREYSCALE.grey30};
          border-style: solid;
          border-width: ${BORDER_WIDTH.none} ${BORDER_WIDTH.xs} ${BORDER_WIDTH.xs}${
    BORDER_WIDTH.none
  };
          vertical-align: middle;

          :last-of-type {
            border-right-width: ${BORDER_WIDTH.none};
          }
        }

        p {
          margin: ${SPACING.none};
        }

        ul {
          margin-block-end: ${SPACING.none};
        }

        tr:last-of-type td {
          border-bottom-width: ${BORDER_WIDTH.none};
        }

        td:nth-last-of-type(2):first-of-type {
          ${tableHeadings}
          border-right-width: ${BORDER_WIDTH.sm};
        }

        @supports selector(:has(b)) {
          td:has(b) {
            ${tableHeadings}
            border-bottom-width: ${BORDER_WIDTH.sm};
          }
        }
      }
  `,
};

export type IntercomArticleModalProps = {
  url: string;
  open: boolean;
  onClose?: () => void;
  onLocationChange?: (url: string) => void;
};

export default function IntercomArticleModal({
  url,
  open,
  onClose,
  onLocationChange,
}: IntercomArticleModalProps) {
  const modalRef = useRef<ModalRef>(null);
  const [articleNode, setArticleNode] = useState<HTMLDivElement | null>(null);
  const [currentUrl, setCurrentUrl] = useState(url);
  const [fetchArticle, { data, error, loading }] = useLazyIntercomArticleQuery({
    errorPolicy: 'all',
    onCompleted: () => modalRef.current?.scrollToTop(),
  });

  useEffect(() => setCurrentUrl(url), [url]);

  useEffect(() => {
    const linkClickHandler = (event: MouseEvent) => {
      const targetLink = (event.target as HTMLElement | null)?.closest('a') || null;

      if (
        targetLink &&
        targetLink.target !== '_blank' &&
        targetLink.hostname === new URL(currentUrl).hostname
      ) {
        event.preventDefault();
        setCurrentUrl(targetLink.href);

        if (onLocationChange) {
          onLocationChange(targetLink.href);
        }
      }
    };

    articleNode?.addEventListener('click', linkClickHandler);

    return () => {
      articleNode?.removeEventListener('click', linkClickHandler);
    };
  }, [articleNode, currentUrl, onLocationChange]);

  useEffect(() => {
    if (open && currentUrl !== '') {
      fetchArticle({
        variables: { url: new URL(currentUrl).pathname },
      });
    }
  }, [open, currentUrl, fetchArticle]);

  return (
    <Modal
      ref={modalRef}
      width={900}
      open={open}
      onClose={() => {
        if (onClose) onClose();

        // Reset to initial url
        setCurrentUrl(url);
      }}
    >
      <div style={{ minHeight: 330 }}>
        {loading && <PageLoading height="330px" />}
        {!loading && error && <Alert variant="danger">{convertBbcode(error.message)}</Alert>}
        {!loading && data?.intercomArticle && (
          <Styled.IntercomArticle
            ref={setArticleNode}
            dangerouslySetInnerHTML={{ __html: data.intercomArticle }}
          />
        )}
      </div>
    </Modal>
  );
}
